import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Building2, Users, Brain, Shield, Workflow, CheckCircle, Target, User, AlertTriangle, Lock, Users2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import JourneyTab from "@/components/wisdom/JourneyTab";
const Intro = () => {
  const navigate = useNavigate();
  const enterpriseFeatures = [{
    icon: Brain,
    title: "Enterprise-grade SLM",
    description: "AI that understands your business the way you do"
  }, {
    icon: Shield,
    title: "Explainability",
    description: "Contributions from AI are tied back to human counterparts"
  }, {
    icon: Workflow,
    title: "Competitive Advantage",
    description: "AI paired together with the wisdom of your workforce"
  }, {
    icon: null,
    title: "Knowledge Network",
    description: "Communities of practice that derisk enterprise AI",
    customIcon: "/lovable-uploads/a6c50459-65a4-4828-9d71-587d98069de4.png"
  }];
  const workforceFeatures = [{
    title: "Human Authority",
    description: "Human experts maintain authority over critical decisions",
    useGibletIcon: true
  }, {
    title: "AI-Human Symbiosis",
    description: "Aligned AI amplifies human capability and possibility",
    useOwlIcon: true
  }, {
    title: "Attribution and Agency",
    description: "People get credit for their contributions with AI",
    useShieldIcon: true
  }, {
    title: "Curated Wisdom",
    description: "Giblet facilitates the knowledge network and marketplace",
    useOwlIcon: true
  }];
  const priorityPrinciples = [{
    icon: Brain,
    title: "Attribution",
    description: "AI today is consuming I.P. without attributing owners or ensuring its accuracy"
  }, {
    icon: Lock,
    title: "Alignment",
    description: "Human I.P. is not digitally usable, sharable, or valued by AI to solve complex challenges"
  }, {
    icon: Users2,
    title: "Incentives",
    description: "The workforce is not incentivized to contribute to AI, and the clock is ticking until they depart"
  }];
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2;
      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center mb-32 mt-48">
          {/* Replace the large image with image and quote side by side */}
          <div className="flex items-center justify-center gap-6 max-w-4xl mx-auto mb-12">
            <div className="flex-shrink-0">
              <img src="/lovable-uploads/db101999-0424-4891-820e-e4956249ca87.png" alt="Sam Altman at TED2025" className="max-w-sm w-full h-auto rounded-lg shadow-lg" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-gray-600 italic leading-relaxed font-medium text-xl">
                "I also believe that we probably do need to figure out some sort of new model around the economics of creative output... I think there are incredible new business models that we and others are excited to explore. Exactly what that's going to look like, I'm not sure."
              </p>
            </div>
          </div>
          
          <div className="flex justify-center mt-16 mb-80">
            <Button onClick={() => scrollToSection("main-intro")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">
              Meet iSPAI
            </Button>
          </div>
        </motion.div>

        <motion.div id="main-intro" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12">
          <div className="flex justify-center mb-6">
            
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">It's going to look exactly like iSPAI.</h1>
          
          {/* Replace the two side-by-side images with single centered image */}
          <div className="flex justify-center my-8">
            <img src="/lovable-uploads/b1570414-6658-4af6-abc8-60b04a637c67.png" alt="iSPAI AI-Agent and Knowledge Network Overview" className="max-w-5xl w-full h-auto object-scale-down" />
          </div>
          
          {/* First section button */}
          <div className="flex justify-center mt-8 mb-64">
            <Button onClick={() => scrollToSection("enterprise-overview")} size="lg" className="bg-black text-white hover:bg-gray-800 text-lg">Enterprise and Workforce Perspectives</Button>
          </div>
        </motion.div>

        <section id="enterprise-overview" style={{
        minHeight: '38vh'
      }}>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl font-bold">
                <Building2 className="h-8 w-8 text-black bg-white rounded p-1" />
                Enterprise Perspective (Demand)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {enterpriseFeatures.map((feature, index) => <motion.div key={feature.title} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-start space-x-4 p-4 bg-white rounded-lg border px-[8px] py-0">
                    {feature.customIcon ? <img src={feature.customIcon} alt={feature.title} className="h-16 w-16 text-black bg-white rounded p-1 flex-shrink-0 mt-1" /> : <feature.icon className="h-8 w-8 text-black bg-white rounded p-1 flex-shrink-0 mt-1" />}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2 text-lg">{feature.title}</h3>
                      <p className="text-gray-600 text-base">{feature.description}</p>
                    </div>
                  </motion.div>)}
              </div>
            </CardContent>
          </Card>

          <div className="mb-6 p-4 border border-slate-950 bg-slate-300 rounded-lg px-[32px] my-[15px] mx-[68px] py-[2px]">
            <h4 className="mb-2 text-lg text-slate-950 font-bold">Why Attribution First?</h4>
            <p className="text-base text-slate-950">
              In enterprise environments, trust and accountability are paramount. By establishing clear attribution 
              chains before implementing automation, we ensure that every AI decision can be traced back to its 
              source, validated by experts, and audited for compliance.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl font-bold">
                <Users className="h-8 w-8 text-black bg-white rounded p-1" />
                Workforce Perspective (Supply)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {workforceFeatures.map((feature, index) => <motion.div key={feature.title} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-start space-x-4 p-4 bg-white rounded-lg border px-[8px] py-0">
                    {feature.useShieldIcon ? <Shield className="h-8 w-8 text-black bg-white rounded p-1 flex-shrink-0 mt-1" /> : feature.useOwlIcon ? <div className="h-16 w-16 bg-white rounded p-1 flex items-center justify-center flex-shrink-0 mt-1"><img src="/lovable-uploads/f3c3c2e4-1c09-4b7f-b94a-fd280f52795a.png" alt="Owl Icon" className="h-12 w-12" /></div> : <User className="h-8 w-8 text-black bg-white rounded p-1 flex-shrink-0 mt-1" />}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2 text-lg">{feature.title}</h3>
                      <p className="text-gray-600 text-base">{feature.description}</p>
                    </div>
                  </motion.div>)}
              </div>
            </CardContent>
          </Card>
          
          <div className="flex justify-center mt-12 mb-48">
            <Button onClick={() => scrollToSection("core-principles")} className="bg-black text-white hover:bg-gray-800 text-lg" size="lg">Problems</Button>
          </div>
        </section>

        <section id="core-principles" style={{
        minHeight: '100vh'
      }} className="space-y-6 mb-32 mt-64 my-[250px]">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl font-bold">Core Problem - AI's knowledge is too general to properly support the enterprise.</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {priorityPrinciples.map((principle, index) => <motion.div key={principle.title} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-start space-x-4 p-6 bg-white rounded-lg border">
                    <principle.icon className="h-8 w-8 text-black bg-white rounded p-1 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2 text-xl">{principle.title}</h3>
                      <p className="text-gray-700 text-lg">{principle.description}</p>
                    </div>
                  </motion.div>)}
              </div>
            </CardContent>
          </Card>
          
          <div className="flex justify-center mt-32 mb-32">
            <Button onClick={() => scrollToSection("implementation")} className="bg-black text-white hover:bg-gray-800 text-lg" size="lg">Bottom Line</Button>
          </div>
        </section>

        

        <section id="implementation" className="space-y-6 mb-64" style={{
        minHeight: '100vh'
      }}>
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl font-bold">The Bottom Line</CardTitle>
            </CardHeader>
            <CardContent>
              <JourneyTab />
            </CardContent>
          </Card>
          
          <div className="flex justify-center gap-4 mt-16">
            <Button onClick={() => {
            navigate("/invest");
            window.scrollTo(0, 0);
          }} className="bg-black text-white hover:bg-gray-800 text-lg" size="lg">Invest</Button>
            <Button onClick={() => window.open("https://www.ispai.io/schedule-demo", "_blank")} className="bg-black text-white hover:bg-gray-800 text-lg" size="lg">Demo</Button>
          </div>
          
          <div className="flex justify-center mt-24 py-[5px]">
            <img src="/lovable-uploads/5b8c2fe7-95e1-4db7-b847-da4c2c4312a9.png" alt="iSPAI Logo" className="h-48 w-auto opacity-80" />
          </div>
        </section>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-12 text-center">
          
        </motion.div>
      </main>
    </div>;
};
export default Intro;